import { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { BTN, btn } from "@/components/common/btn";

// "+ Scope": tick one or many library scopes, add them all at once.
export function AddScopesButton({ masterScopes, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(() => new Set());

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (masterScopes || []).filter((ms) => !s || ms.name.toLowerCase().includes(s));
  }, [masterScopes, q]);

  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const reset = () => { setQ(""); setPicked(new Set()); };
  const add = () => {
    onAdd(masterScopes.filter((ms) => picked.has(ms.id)));
    reset(); setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" className={BTN.write} data-testid="btn-add-scope">
          <Plus className="w-3.5 h-3.5 mr-1" /> Scope
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid="add-scope-popover">
        <div className="flex items-center gap-2 border-b border-slate-200 px-2 py-1.5">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search scopes…" className="h-7 flex-1 bg-transparent text-xs outline-none placeholder:text-slate-400" data-testid="add-scope-search" />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {list.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">{masterScopes?.length ? "No match." : "No scopes in library. Add scopes in Equipment Library."}</div>}
          {list.map((ms) => (
            <label key={ms.id} className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 ${picked.has(ms.id) ? "bg-emerald-50" : ""}`} data-testid={`scope-option-${ms.name}`}>
              <input type="checkbox" checked={picked.has(ms.id)} onChange={() => toggle(ms.id)} className="h-3.5 w-3.5 accent-emerald-600" data-testid={`scope-pick-${ms.id}`} />
              <span className="truncate">{ms.name}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-2 py-1.5">
          <span className="text-[11px] text-slate-500" data-testid="add-scope-count">{picked.size ? `${picked.size} selected` : "Tick one or more"}</span>
          <button type="button" className={btn("write")} disabled={!picked.size} onClick={add} data-testid="btn-add-picked-scopes">Add {picked.size || ""} Scope{picked.size === 1 ? "" : "s"}</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
